import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { POSTS } from "@/pages/Blog";
import {
  Download,
  Eye,
  KeyRound,
  Mail,
  MailCheck,
  Pencil,
  Rss,
  Save,
  Search,
  Send,
  Server,
  Sparkles,
  StickyNote,
  Trash2,
  Users as UsersIcon,
  Wand2,
  X,
} from "lucide-react";

function StatCard({ label, value, testId }) {
  return (
    <div className="ccs-card flex-1 min-w-[140px]">
      <div className="ccs-label mb-1">{label}</div>
      <div className="mono text-3xl font-semibold" data-testid={testId}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "Never";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
  } catch (_) { return iso?.slice(0, 10); }
}

// 12-character mixed-case password without ambiguous characters — same
// generator the per-club admin uses, kept identical so support runbooks
// can be shared between the two screens.
function generatePassword(length = 12) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
  const out = [];
  const cryptoObj = window.crypto || window.msCrypto;
  if (cryptoObj && cryptoObj.getRandomValues) {
    const bytes = new Uint32Array(length);
    cryptoObj.getRandomValues(bytes);
    for (let i = 0; i < length; i++) out.push(chars[bytes[i] % chars.length]);
  } else {
    for (let i = 0; i < length; i++) out.push(chars[Math.floor(Math.random() * chars.length)]);
  }
  return out.join("");
}

const ROLE_OPTIONS = [
  { value: "SUPERADMIN", label: "SUPERADMIN — platform owner" },
  { value: "ADMIN", label: "ADMIN — full control of one club" },
  { value: "SECRETARY", label: "SECRETARY — score competitions" },
  { value: "VIEWER", label: "VIEWER — read-only" },
  { value: "MEMBER", label: "MEMBER — read-only + own results" },
];

export default function Platform() {
  const { user: me } = useAuth();
  const [overview, setOverview] = useState(null);
  // {data, loading} shape — see Phase 57 RCA. Using a plain null sentinel
  // previously caused a black-screen regression when one renderer read
  // `clubs.length` before the API resolved. Keeping the shape uniform makes
  // it impossible to accidentally call .length on a non-array.
  const [clubs, setClubs] = useState({ data: [], loading: true });
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  // "clubs" | "users" | "email" | "subscribers". Initialised from the URL
  // so /platform/subscribers deep-links straight to the subscribers tab
  // (the tab pattern predates real sub-routes, so we fake it with location).
  const location = useLocation();
  const initialTab = location.pathname.endsWith("/subscribers")
    ? "subscribers"
    : location.pathname.endsWith("/assets")
      ? "assets"
      : "clubs";
  const [tab, setTab] = useState(initialTab);
  const [err, setErr] = useState("");
  const [confirmingClub, setConfirmingClub] = useState(null); // {id, name}
  const [viewing, setViewing] = useState(null);   // user being viewed (read-only)
  const [editing, setEditing] = useState(null);   // user being edited
  const [resetting, setResetting] = useState(null); // user whose password is being reset

  const load = async () => {
    try {
      const [o, c, u] = await Promise.all([
        api.get("/platform/overview"),
        api.get("/platform/clubs"),
        api.get("/platform/users"),
      ]);
      setOverview(o.data);
      setClubs({ data: c.data, loading: false });
      setUsers(u.data);
      setErr("");
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  useEffect(() => { load(); }, []);

  const visibleClubs = useMemo(() => {
    if (clubs.loading) return { data: [], loading: true };
    const q = query.trim().toLowerCase();
    const filtered = q
      ? clubs.data.filter((c) => (c.name || "").toLowerCase().includes(q))
      : clubs.data;
    return { data: filtered, loading: false };
  }, [clubs, query]);

  const visibleUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.email || "").toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q) ||
        (u.club_name || "").toLowerCase().includes(q) ||
        (u.role || "").toLowerCase().includes(q)
    );
  }, [users, query]);

  const deleteUser = async (u) => {
    if (me?.id === u.id) {
      alert("You can't delete your own SUPERADMIN account.");
      return;
    }
    if (!window.confirm(`Delete user ${u.email} (${u.club_name})? This cannot be undone.`)) return;
    try {
      await api.delete(`/platform/users/${u.id}`);
      load();
    } catch (ex) {
      alert(formatApiError(ex.response?.data?.detail));
    }
  };

  const markVerified = async (u) => {
    if (u.email_verified) return;
    if (!window.confirm(
      `Force-mark ${u.email} as verified? Only do this when SMTP is throttled or the user genuinely can't receive the email.`
    )) return;
    try {
      await api.post(`/platform/users/${u.id}/mark-verified`);
      // Optimistic local patch — avoids re-fetching all 1000s of users
      // just to flip one boolean on one row.
      setUsers((list) =>
        list.map((row) => (row.id === u.id ? { ...row, email_verified: true } : row))
      );
    } catch (ex) {
      alert(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const downloadEmails = () => {
    // Cache-bust so re-clicks don't serve a stale CSV from disk cache.
    window.location.href = `${api.defaults.baseURL}/platform/emails.csv?t=${Date.now()}`;
  };

  const resetClubAdminPassword = async (admin, clubName) => {
    const pw = generatePassword(12);
    if (!window.confirm(
      `Reset ${admin.email} (admin of "${clubName}") to a new password?\n\n` +
      `The new password will be copied to your clipboard so you can email it to them.\n\n` +
      `New password preview: ${pw}`
    )) return;
    try {
      await api.post(`/platform/users/${admin.id}/reset-password`, { password: pw });
      try { await navigator.clipboard.writeText(pw); } catch (_) { /* ignore */ }
      alert(
        `✓ Password reset for ${admin.email}.\n\n` +
        `New password (also copied to clipboard):\n${pw}\n\n` +
        `Paste it into your message to them. They should change it after they log in.`
      );
    } catch (ex) {
      alert(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl">
      <div className="flex items-center gap-3 mb-6">
        <Server size={20} className="text-amber-400" />
        <div>
          <div className="ccs-label mb-1">Platform admin</div>
          <h1 className="text-4xl font-semibold tracking-tight">All clubs &amp; users</h1>
        </div>
      </div>

      {err && <div className="danger-text text-sm mb-3">{err}</div>}

      <div className="flex gap-3 mb-6 flex-wrap" data-testid="platform-stats">
        <StatCard label="Clubs" value={overview?.clubs} testId="stat-clubs" />
        <StatCard label="Users" value={overview?.users} testId="stat-users" />
        <StatCard label="Photographers" value={overview?.photographers} testId="stat-photographers" />
        <StatCard label="Competitions" value={overview?.competitions} testId="stat-competitions" />
        <StatCard label="Entries" value={overview?.entries} testId="stat-entries" />
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-2">
          <button
            onClick={() => setTab("clubs")}
            className={"text-sm px-3 py-1.5 border " + (tab === "clubs" ? "border-amber-400 text-amber-400" : "border-neutral-700 muted-text")}
            data-testid="tab-clubs"
          >
            <Server size={14} className="inline mr-1.5" /> Clubs ({clubs.data.length})
          </button>
          <button
            onClick={() => setTab("users")}
            className={"text-sm px-3 py-1.5 border " + (tab === "users" ? "border-amber-400 text-amber-400" : "border-neutral-700 muted-text")}
            data-testid="tab-users"
          >
            <UsersIcon size={14} className="inline mr-1.5" /> Users ({users.length})
          </button>
          <button
            onClick={() => setTab("email")}
            className={"text-sm px-3 py-1.5 border " + (tab === "email" ? "border-amber-400 text-amber-400" : "border-neutral-700 muted-text")}
            data-testid="tab-email"
          >
            <Mail size={14} className="inline mr-1.5" /> Email delivery
          </button>
          <button
            onClick={() => setTab("subscribers")}
            className={"text-sm px-3 py-1.5 border " + (tab === "subscribers" ? "border-amber-400 text-amber-400" : "border-neutral-700 muted-text")}
            data-testid="tab-subscribers"
          >
            <Rss size={14} className="inline mr-1.5" /> Blog subscribers
          </button>
          <button
            onClick={() => setTab("assets")}
            className={"text-sm px-3 py-1.5 border " + (tab === "assets" ? "border-amber-400 text-amber-400" : "border-neutral-700 muted-text")}
            data-testid="tab-assets"
          >
            <StickyNote size={14} className="inline mr-1.5" /> Assets
          </button>
        </div>
        <button
          onClick={downloadEmails}
          className="ccs-btn-ghost flex items-center gap-2 text-sm"
          data-testid="download-emails-button"
        >
          <Download size={14} /> Download all emails (CSV)
        </button>
      </div>

      <div className={"mb-4 relative max-w-md " + (tab === "email" || tab === "subscribers" || tab === "assets" ? "hidden" : "")}>
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 muted-text pointer-events-none" />
        <input
          className="ccs-input pl-9"
          placeholder={tab === "clubs" ? "Search clubs…" : "Search users by name, email, club, role…"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="platform-search"
        />
      </div>

      {tab === "clubs" && (
        <div className="ccs-card p-0 overflow-x-auto">
          <table className="ccs-table">
            <thead>
              <tr>
                <th>Club</th>
                <th>Admin</th>
                <th>Users</th>
                <th>Photographers</th>
                <th>Competitions</th>
                <th>Entries</th>
                <th>Created</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {visibleClubs.loading ? (
                <tr>
                  <td colSpan="8" className="muted-text text-center py-8" data-testid="clubs-loading">
                    Loading clubs… (this can take a few seconds on busy platforms)
                  </td>
                </tr>
              ) : (
                <>
              {visibleClubs.data.map((c) => (
                <tr key={c.id} data-testid={`club-row-${c.id}`}>
                  <td className="font-medium">{c.name}</td>
                  <td data-testid={`club-admin-${c.id}`}>
                    {(c.admins || []).length === 0 ? (
                      <span className="muted-text text-xs">No admin</span>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {(c.admins || []).map((a) => (
                          <div key={a.id} className="flex items-center gap-2 text-xs">
                            <div className="flex flex-col min-w-0">
                              <a
                                href={`mailto:${a.email}`}
                                className="mono text-amber-400 hover:underline truncate max-w-[220px]"
                                title={a.name ? `${a.name} <${a.email}>` : a.email}
                              >
                                {a.email}
                              </a>
                              <span
                                className="muted-text text-[10px]"
                                data-testid={`club-admin-last-login-${a.id}`}
                              >
                                {a.last_login_at
                                  ? `last login ${fmtDate(a.last_login_at)}`
                                  : "never logged in"}
                              </span>
                            </div>
                            <button
                              onClick={() => resetClubAdminPassword(a, c.name)}
                              className="p-1 muted-text hover:text-white hover:bg-neutral-800"
                              title="Reset this admin's password"
                              data-testid={`reset-admin-password-${a.id}`}
                            >
                              <KeyRound size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="mono">{c.user_count}</td>
                  <td className="mono">{c.photographer_count}</td>
                  <td className="mono">{c.competition_count}</td>
                  <td className="mono">{c.entry_count}</td>
                  <td className="muted-text text-xs">{fmtDate(c.created_at)}</td>
                  <td className="text-right">
                    <button
                      onClick={() => setConfirmingClub(c)}
                      className="p-2 text-red-400 hover:bg-neutral-800"
                      title="Delete this club and ALL its data"
                      disabled={me?.club_id === c.id}
                      data-testid={`delete-club-${c.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {visibleClubs.data.length === 0 && !visibleClubs.loading && (
                <tr><td colSpan="8" className="muted-text text-center py-8">No clubs match your search.</td></tr>
              )}
              </>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "users" && (
        <div className="ccs-card p-0 overflow-x-auto">
          <table className="ccs-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Club</th>
                <th>Status</th>
                <th>Last login</th>
                <th>Created</th>
                <th style={{ width: 170 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map((u) => {
                const isSelf = me?.id === u.id;
                const active = u.active !== false;
                return (
                  <tr key={u.id} data-testid={`platform-user-${u.id}`}>
                    <td className="mono">
                      {u.email}
                      {isSelf && <span className="ml-2 tag" style={{ background: "#2a1a05", color: "#fbbf24" }}>you</span>}
                    </td>
                    <td>{u.name}</td>
                    <td><span className="tag">{u.role}</span></td>
                    <td className="muted-text">{u.club_name}</td>
                    <td>
                      {active ? (
                        <span className="tag" style={{ background: "#0d2b1a", color: "#4ade80" }}>Active</span>
                      ) : (
                        <span className="tag" style={{ background: "#2b0d0d", color: "#f87171" }}>Disabled</span>
                      )}
                      {u.email_verified === false && (
                        <span
                          className="tag ml-1"
                          style={{ background: "#2a1a05", color: "#fbbf24" }}
                          title="Hasn't clicked the verification link yet"
                          data-testid={`platform-unverified-${u.id}`}
                        >
                          Unverified
                        </span>
                      )}
                    </td>
                    <td className="muted-text text-xs">{fmtDate(u.last_login_at)}</td>
                    <td className="muted-text text-xs">{(u.created_at || "").slice(0, 10)}</td>
                    <td className="text-right whitespace-nowrap">
                      <button
                        onClick={() => setViewing(u)}
                        className="p-2 hover:bg-neutral-800"
                        title="View user details"
                        data-testid={`platform-view-user-${u.id}`}
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => setEditing({ ...u, password: "" })}
                        className="p-2 hover:bg-neutral-800"
                        title="Edit user"
                        data-testid={`platform-edit-user-${u.id}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => setResetting(u)}
                        className="p-2 hover:bg-neutral-800"
                        title="Reset password"
                        data-testid={`platform-reset-pw-${u.id}`}
                      >
                        <KeyRound size={14} />
                      </button>
                      <button
                        onClick={() => markVerified(u)}
                        disabled={u.email_verified === true}
                        className={
                          "p-2 hover:bg-neutral-800 disabled:opacity-30 " +
                          (u.email_verified === false ? "text-amber-400" : "")
                        }
                        title={u.email_verified ? "Already verified" : "Force-mark email as verified (SMTP escape hatch)"}
                        data-testid={`platform-mark-verified-${u.id}`}
                      >
                        <MailCheck size={14} />
                      </button>
                      <button
                        onClick={() => deleteUser(u)}
                        disabled={isSelf}
                        className="p-2 text-red-400 hover:bg-neutral-800 disabled:opacity-30"
                        title={isSelf ? "Can't delete yourself" : "Delete user"}
                        data-testid={`platform-delete-user-${u.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {visibleUsers.length === 0 && (
                <tr><td colSpan="8" className="muted-text text-center py-8">No users match your search.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "email" && (
        <EmailDeliveryTab />
      )}

      {tab === "subscribers" && (
        <SubscribersTab />
      )}

      {tab === "assets" && (
        <AssetsTab />
      )}

      {confirmingClub && (
        <DeleteClubDialog
          club={confirmingClub}
          onClose={() => setConfirmingClub(null)}
          onDone={() => { setConfirmingClub(null); load(); }}
        />
      )}

      {viewing && (
        <ViewUserDialog user={viewing} onClose={() => setViewing(null)} />
      )}

      {editing && (
        <EditUserDialog
          user={editing}
          me={me}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); load(); }}
        />
      )}

      {resetting && (
        <ResetPasswordDialog
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={() => setResetting(null)}
        />
      )}
    </div>
  );
}

function DeleteClubDialog({ club, onClose, onDone }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.delete(`/platform/clubs/${club.id}`, {
        data: { confirm_name: typed },
      });
      alert(
        `Deleted "${data.club_name}": ${data.deleted.users} users, ${data.deleted.photographers} photographers, ` +
        `${data.deleted.competitions} competitions, ${data.deleted.entries} entries.`
      );
      onDone();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dialog-bg" onClick={onClose}>
      <form className="dialog-panel p-6" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold danger-text">Delete club</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="text-sm mb-4">
          You&rsquo;re about to <span className="danger-text font-medium">permanently delete</span>{" "}
          <span className="mono">{club.name}</span> and all of its data:
          <ul className="list-disc pl-5 mt-2 muted-text text-xs space-y-0.5">
            <li>{club.user_count} user account(s)</li>
            <li>{club.photographer_count} photographer record(s)</li>
            <li>{club.competition_count} competition(s)</li>
            <li>{club.entry_count} entry/scoring record(s)</li>
          </ul>
          <div className="muted-text text-xs mt-2">This cannot be undone.</div>
        </div>

        <div className="form-row">
          <label className="ccs-label">
            Type <span className="mono text-amber-400">{club.name}</span> to confirm
          </label>
          <input
            className="ccs-input mono"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            data-testid="delete-club-confirm-input"
            autoFocus
          />
        </div>

        {err && <div className="danger-text text-sm">{err}</div>}

        <div className="flex justify-end gap-3 mt-4">
          <button type="button" onClick={onClose} className="ccs-btn-ghost">Cancel</button>
          <button
            type="submit"
            disabled={busy || typed.trim() !== club.name}
            className="ccs-btn"
            style={{ background: "#7f1d1d", borderColor: "#7f1d1d", color: "#fff" }}
            data-testid="delete-club-confirm-button"
          >
            {busy ? "Deleting…" : `Delete ${club.name}`}
          </button>
        </div>
      </form>
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div className="form-row" style={{ marginBottom: 8 }}>
      <div className="ccs-label">{label}</div>
      <div className={mono ? "mono text-sm" : "text-sm"}>{value || <span className="muted-text">—</span>}</div>
    </div>
  );
}

function ViewUserDialog({ user, onClose }) {
  return (
    <div className="dialog-bg" onClick={onClose}>
      <form
        className="dialog-panel p-6"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => e.preventDefault()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-semibold">User details</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <InfoRow label="Name" value={user.name} />
        <InfoRow label="Email" value={user.email} mono />
        <InfoRow label="Role" value={user.role} mono />
        <InfoRow label="Club" value={user.club_name} />
        <InfoRow label="Status" value={user.active === false ? "Disabled" : "Active"} />
        <InfoRow label="Last login" value={fmtDate(user.last_login_at)} />
        <InfoRow label="Created" value={(user.created_at || "").slice(0, 10)} mono />
        <InfoRow label="User ID" value={user.id} mono />
        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="ccs-btn-ghost" data-testid="view-user-close">Close</button>
        </div>
      </form>
    </div>
  );
}

function EditUserDialog({ user, me, onClose, onDone }) {
  const [form, setForm] = useState({
    email: user.email || "",
    name: user.name || "",
    role: user.role || "VIEWER",
    active: user.active !== false,
    password: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isSelf = me?.id === user.id;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const payload = {};
      if (form.email !== user.email) payload.email = form.email;
      if (form.name !== user.name) payload.name = form.name;
      if (form.role !== user.role) payload.role = form.role;
      if (form.active !== (user.active !== false)) payload.active = form.active;
      if (form.password) payload.password = form.password;
      if (Object.keys(payload).length === 0) {
        onDone();
        return;
      }
      await api.patch(`/platform/users/${user.id}`, payload);
      onDone();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dialog-bg" onClick={onClose}>
      <form className="dialog-panel p-6" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-semibold">Edit user</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="form-row">
          <label className="ccs-label">Email *</label>
          <input
            required type="email" className="ccs-input"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="platform-edit-email"
          />
          {form.email !== user.email && (
            <div className="muted-text text-xs mt-1">
              ⚠ Changing the email will sign this user out of any active session.
            </div>
          )}
        </div>
        <div className="form-row">
          <label className="ccs-label">Name *</label>
          <input
            required className="ccs-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="platform-edit-name"
          />
        </div>
        <div className="form-row">
          <label className="ccs-label">Role *</label>
          <select
            className="ccs-input"
            value={form.role}
            disabled={isSelf}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="platform-edit-role"
          >
            {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {isSelf && (
            <div className="muted-text text-xs mt-1">
              You can&rsquo;t demote your own SUPERADMIN role here.
            </div>
          )}
        </div>
        <div className="form-row">
          <label className="ccs-label">Optional: change password</label>
          <input
            type="text"
            className="ccs-input mono"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Leave blank to keep the current password"
            data-testid="platform-edit-password"
          />
          <div className="muted-text text-xs mt-1">
            Tip: use the row&rsquo;s key icon for a quick-and-copy reset.
          </div>
        </div>
        <div className="form-row">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="accent-amber-400"
              checked={form.active}
              disabled={isSelf}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              data-testid="platform-edit-active"
            />
            <span className="text-sm">Account is active</span>
          </label>
        </div>
        {err && <div className="danger-text text-sm">{err}</div>}
        <div className="flex justify-end gap-3 mt-5">
          <button type="button" onClick={onClose} className="ccs-btn-ghost">Cancel</button>
          <button type="submit" disabled={busy} className="ccs-btn" data-testid="platform-edit-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ResetPasswordDialog({ user, onClose }) {
  const [password, setPassword] = useState("");
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const generate = async () => {
    const pw = generatePassword(12);
    setPassword(pw);
    try {
      await navigator.clipboard.writeText(pw);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      // clipboard may be blocked — operator can still copy from the field.
    }
  };

  const copyAgain = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      // clipboard may be blocked — silent fallback.
    }
  };

  const save = async () => {
    if (!password || password.length < 6) {
      setErr("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post(`/platform/users/${user.id}/reset-password`, { password });
      setSaved(true);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dialog-bg" onClick={onClose}>
      <div className="dialog-panel p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">Reset password</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="text-sm mb-4">
          Setting a new password for <span className="mono">{user.email}</span> in club{" "}
          <span className="font-medium">{user.club_name}</span>. Send the new password to
          them — they can change it themselves once logged in.
        </div>
        <div className="form-row">
          <label className="ccs-label">New password</label>
          <div className="flex gap-2">
            <input
              type="text"
              minLength="6"
              className="ccs-input mono flex-1"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setSaved(false); }}
              placeholder="Click Generate, or type one"
              data-testid="reset-password-input"
            />
            <button
              type="button"
              onClick={generate}
              className="ccs-btn-ghost flex items-center gap-1 text-xs whitespace-nowrap"
              data-testid="reset-password-generate"
            >
              <Wand2 size={14} /> Generate
            </button>
          </div>
          {copied && (
            <div className="success-text text-xs mt-1">
              ✓ Copied to clipboard — paste it into your message to {user.name}.
            </div>
          )}
        </div>
        {err && <div className="danger-text text-sm">{err}</div>}
        {saved ? (
          <div className="success-text text-sm">
            ✓ Password updated. Make sure you&rsquo;ve sent it to the user before closing.
          </div>
        ) : (
          <div className="muted-text text-xs">
            Click <b>Save</b> to apply. The old password will stop working immediately.
          </div>
        )}
        <div className="flex justify-end gap-3 mt-4">
          {saved && password && (
            <button
              type="button"
              onClick={copyAgain}
              className="ccs-btn-ghost flex items-center gap-1 text-sm"
              data-testid="reset-password-copy-again"
            >
              <Eye size={14} /> Copy again
            </button>
          )}
          <button type="button" onClick={onClose} className="ccs-btn-ghost">
            {saved ? "Done" : "Cancel"}
          </button>
          {!saved && (
            <button
              type="button"
              disabled={busy || !password}
              onClick={save}
              className="ccs-btn"
              data-testid="reset-password-save"
            >
              {busy ? "Saving…" : "Save new password"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}



// Live pull from Brevo's REST v3 API — surfaces bounce/open/delivery counts
// across every tenant so a SUPERADMIN can catch a dying provider before
// club admins start filing tickets. The endpoint is /platform/email-stats;
// see routes/platform.py for the upstream wiring.

function SubscribersTab() {
  // Admin view over the `blog_subscribers` Mongo collection. Read-only
  // except for delete — the subscribe endpoint is public and owned by
  // Blog.jsx, so this screen exists purely for visibility + CSV export.
  const [state, setState] = useState({ loading: true, data: null, err: "" });
  const [query, setQuery] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [announcing, setAnnouncing] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true, err: "" }));
    try {
      const { data } = await api.get("/platform/subscribers");
      setState({ loading: false, data, err: "" });
    } catch (ex) {
      setState({
        loading: false,
        data: null,
        err: formatApiError(ex.response?.data?.detail) || ex.message,
      });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const download = () => {
    // Direct fetch → blob → anchor click. Mirrors the pattern used by the
    // other Platform CSV exports so the auth header travels correctly.
    const url = `${api.defaults.baseURL}/platform/subscribers.csv`;
    const token = localStorage.getItem("ccs_token");
    fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then((r) => r.blob())
      .then((b) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(b);
        link.download = "blog-subscribers.csv";
        document.body.appendChild(link);
        link.click();
        link.remove();
      });
  };

  const remove = async (email) => {
    setDeleting(email);
    try {
      await api.delete(`/platform/subscribers/${encodeURIComponent(email)}`);
      await load();
    } catch (ex) {
      setState((s) => ({ ...s, err: formatApiError(ex.response?.data?.detail) || ex.message }));
    } finally {
      setDeleting(null);
    }
  };

  const rows = state.data?.subscribers || [];
  const filtered = rows.filter(
    (r) => !query || (r.email || "").toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <div data-testid="subscribers-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-4 text-sm">
          <div>
            <span className="ccs-label mr-2">Total</span>
            <span className="mono text-white" data-testid="subscribers-total">
              {state.data?.total ?? "—"}
            </span>
          </div>
          <div>
            <span className="ccs-label mr-2">Synced to Brevo</span>
            <span className="mono text-emerald-400" data-testid="subscribers-synced">
              {state.data?.brevo_synced ?? "—"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 muted-text pointer-events-none" />
            <input
              className="ccs-input pl-9 text-sm"
              placeholder="Search email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="subscribers-search"
            />
          </div>
          <button
            onClick={download}
            disabled={!rows.length}
            className="ccs-btn-ghost flex items-center gap-2 text-sm disabled:opacity-40"
            data-testid="subscribers-download"
          >
            <Download size={14} /> Download CSV
          </button>
          <button
            onClick={() => setAnnouncing(true)}
            disabled={!rows.length}
            className="ccs-btn flex items-center gap-2 text-sm disabled:opacity-40"
            data-testid="subscribers-announce"
          >
            <Send size={14} /> Send announcement
          </button>
        </div>
      </div>

      {state.err && (
        <div className="ccs-card border-red-500/40 text-red-400 text-sm mb-4" data-testid="subscribers-err">
          {state.err}
        </div>
      )}

      <div className="ccs-card p-0 overflow-x-auto">
        <table className="ccs-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Subscribed</th>
              <th>Source</th>
              <th>Brevo</th>
              <th className="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {state.loading && (
              <tr>
                <td colSpan={5} className="muted-text text-sm py-6 text-center">
                  Loading subscribers…
                </td>
              </tr>
            )}
            {!state.loading && filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="muted-text text-sm py-6 text-center" data-testid="subscribers-empty">
                  {rows.length === 0
                    ? "No subscribers yet. Share /blog to get the first one."
                    : "No matches for that search."}
                </td>
              </tr>
            )}
            {filtered.map((r) => (
              <tr key={r.email} data-testid={`subscriber-row-${r.email}`}>
                <td className="mono text-sm">{r.email}</td>
                <td className="text-sm muted-text">{fmtDate(r.created_at)}</td>
                <td className="text-xs muted-text">{r.source || "—"}</td>
                <td>
                  {r.brevo_synced ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                      <MailCheck size={12} /> Synced
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 text-xs text-amber-400"
                      title={r.brevo_error || "Not synced"}
                    >
                      <Mail size={12} /> Local only
                    </span>
                  )}
                </td>
                <td className="text-right">
                  <button
                    onClick={() => remove(r.email)}
                    disabled={deleting === r.email}
                    className="text-neutral-500 hover:text-red-400 disabled:opacity-40"
                    title="Remove from local store"
                    data-testid={`subscriber-delete-${r.email}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="muted-text text-xs mt-3">
        Deleting a subscriber here removes them from our own store but does
        not remove them from Brevo. Use the Brevo dashboard for that, or
        let the subscriber&apos;s unsubscribe link do the job.
      </p>

      {announcing && (
        <AnnounceDialog
          recipientCount={state.data?.total || 0}
          onClose={() => setAnnouncing(false)}
        />
      )}
    </div>
  );
}


function AssetsTab() {
  // Free-form notes page — just a textarea backed by a single Mongo doc.
  // No versioning, no rich text, no attachments. It's a scratchpad for
  // credentials, plan IDs, domain expiries, and anything else the
  // operator needs to jot down without leaving the app.
  const [content, setContent] = useState("");
  const [savedContent, setSavedContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/platform/assets");
      setContent(data.content || "");
      setSavedContent(data.content || "");
      setUpdatedAt(data.updated_at || null);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const { data } = await api.put("/platform/assets", { content });
      setSavedContent(content);
      setUpdatedAt(data.updated_at || null);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setSaving(false);
    }
  };

  // Keyboard shortcut: Cmd/Ctrl+S saves without needing to hunt for
  // the button. Matches how anyone using a notes app expects it to work.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        if (!saving && content !== savedContent) save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [content, savedContent, saving]);

  const dirty = content !== savedContent;

  return (
    <div data-testid="assets-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="text-sm muted-text">
          Plain-text scratchpad for keys, plan IDs, renewal dates, or
          anything else you want to keep to hand. Only visible to
          superadmins. Press <kbd className="mono text-xs">⌘/Ctrl+S</kbd> to save.
        </div>
        <div className="flex items-center gap-3">
          {updatedAt && (
            <span className="muted-text text-xs" data-testid="assets-updated-at">
              Saved {fmtDate(updatedAt)}
            </span>
          )}
          <button
            onClick={save}
            disabled={saving || !dirty}
            className="ccs-btn flex items-center gap-2 text-sm disabled:opacity-40"
            data-testid="assets-save"
          >
            <Save size={14} />
            {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
        </div>
      </div>

      {err && (
        <div className="ccs-card border-red-500/40 text-red-400 text-sm mb-3" data-testid="assets-err">
          {err}
        </div>
      )}

      <textarea
        className="ccs-input mono text-sm w-full"
        style={{ minHeight: 500, resize: "vertical", lineHeight: 1.55 }}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        disabled={loading}
        placeholder={loading ? "Loading…" : "Start typing…\n\nExample:\n\n== PayPal (production) ==\nClient ID: ...\nWebhook ID: WH-...\nStarter plan (£60): P-...\nUnlimited plan (£80): P-...\n\n== Domain ==\nRegistrar: Namecheap\nExpires: 2027-03-14"}
        data-testid="assets-textarea"
      />
    </div>
  );
}




function AnnounceDialog({ recipientCount, onClose }) {
  // Compose + broadcast a one-off HTML email to every subscriber. The
  // test-send path fires the email to the currently-logged-in
  // superadmin so they can eyeball the result in their own inbox
  // before firing the real broadcast.
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState("");   // "" | "test" | "broadcast"
  const [confirming, setConfirming] = useState(false);
  const [msg, setMsg] = useState({ tone: "", text: "" });

  // Pick the most recent published blog post (same filter as /blog).
  // Used by the "Compose from latest blog post" quick-fill button so
  // the admin can turn a fresh post into a broadcast with one click.
  const latestPost = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const published = POSTS.filter((p) => (p.publish_at || p.date) <= today);
    if (!published.length) return null;
    return published.reduce((newest, p) => {
      const dp = p.publish_at || p.date;
      const dn = newest.publish_at || newest.date;
      return dp > dn ? p : newest;
    });
  }, []);

  const composeFromLatest = () => {
    if (!latestPost) return;
    const url = `https://cameraclubscore.com/blog/${latestPost.slug}`;
    setSubject(`New on the blog: ${latestPost.title}`);
    setBody(
      `<h2>${latestPost.title}</h2>\n` +
      `<p>${latestPost.excerpt}</p>\n` +
      `<p><a href="${url}">Read the full post &rarr;</a></p>\n` +
      `<p style="color:#888;font-size:13px">Roughly a ${latestPost.read_minutes}-minute read.</p>`,
    );
    setMsg({ tone: "", text: "" });
  };

  const canSend = subject.trim().length > 0 && body.trim().length > 0;

  const send = async (testOnly) => {
    setBusy(testOnly ? "test" : "broadcast");
    setMsg({ tone: "", text: "" });
    try {
      const { data } = await api.post("/platform/subscribers/announce", {
        subject: subject.trim(),
        html: body,
        test_only: testOnly,
      });
      if (testOnly) {
        setMsg({
          tone: "ok",
          text: `Test sent to ${data.sent_to}. Check your inbox — the subject will be prefixed with [TEST].`,
        });
      } else {
        setMsg({
          tone: "ok",
          text: `Queued for ${data.recipients} subscribers. Broadcast runs in the background — refresh the tab in a minute to see delivery counts.`,
        });
        setConfirming(false);
      }
    } catch (ex) {
      setMsg({
        tone: "err",
        text: formatApiError(ex.response?.data?.detail) || ex.message,
      });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="dialog-bg" onClick={onClose}>
      <div
        className="dialog-panel p-6 max-w-2xl w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="announce-dialog"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">Send announcement</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>

        <p className="muted-text text-sm mb-4">
          Broadcast an email to <span className="mono text-white">{recipientCount}</span>{" "}
          subscriber(s). An unsubscribe footer is appended automatically —
          you don&apos;t need to include one in the body.
        </p>

        {latestPost && (
          <button
            type="button"
            onClick={composeFromLatest}
            disabled={!!busy}
            className="ccs-btn-ghost text-xs inline-flex items-center gap-1.5 mb-4 disabled:opacity-40"
            data-testid="announce-compose-latest"
            title={`Uses "${latestPost.title}"`}
          >
            <Sparkles size={12} className="text-amber-400" />
            Compose from latest blog post
          </button>
        )}

        <div className="form-row">
          <label className="ccs-label">Subject</label>
          <input
            className="ccs-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="What's new on Camera Club Score"
            maxLength={200}
            disabled={!!busy}
            data-testid="announce-subject"
            autoFocus
          />
        </div>

        <div className="form-row">
          <label className="ccs-label">
            Body (HTML supported)
          </label>
          <textarea
            className="ccs-input mono text-sm"
            style={{ minHeight: 220, resize: "vertical" }}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={"<h2>New this month</h2>\n<p>We shipped ...</p>\n<p><a href=\"https://cameraclubscore.com\">Take a look</a>.</p>"}
            disabled={!!busy}
            data-testid="announce-body"
          />
          <div className="muted-text text-xs mt-1">
            Plain paragraphs render as-is. Use inline HTML for links,
            headings, and lists. Avoid embedded images &mdash; they trip
            spam filters and break in dark mode clients.
          </div>
        </div>

        {msg.text && (
          <div
            className={
              "text-sm mb-3 " + (msg.tone === "ok" ? "text-emerald-400" : "danger-text")
            }
            data-testid="announce-msg"
          >
            {msg.text}
          </div>
        )}

        {confirming ? (
          <div className="ccs-card border-amber-400/40 bg-amber-400/5 mb-3 text-sm">
            <div className="font-medium mb-1">
              Send to all {recipientCount} subscribers?
            </div>
            <div className="muted-text text-xs mb-3">
              This cannot be undone. Each subscriber will receive one
              copy at the address they signed up with, including a
              one-click unsubscribe link.
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={!!busy}
                className="ccs-btn-ghost text-sm"
                data-testid="announce-cancel-broadcast"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => send(false)}
                disabled={!!busy || !canSend}
                className="ccs-btn text-sm"
                data-testid="announce-confirm-broadcast"
              >
                {busy === "broadcast" ? "Sending…" : `Send to ${recipientCount}`}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center gap-3 mt-4">
            <button
              type="button"
              onClick={() => send(true)}
              disabled={!canSend || !!busy}
              className="ccs-btn-ghost text-sm flex items-center gap-2"
              data-testid="announce-test-send"
            >
              <Mail size={14} />
              {busy === "test" ? "Sending test…" : "Send test to me"}
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="ccs-btn-ghost text-sm"
                data-testid="announce-close"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => setConfirming(true)}
                disabled={!canSend || !!busy || recipientCount === 0}
                className="ccs-btn text-sm flex items-center gap-2"
                data-testid="announce-broadcast"
              >
                <Send size={14} /> Send to all {recipientCount}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



function EmailDeliveryTab() {
  const [days, setDays] = useState(30);
  const [state, setState] = useState({ loading: true, data: null, err: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true, err: "" }));
      try {
        const { data } = await api.get(`/platform/email-stats?days=${days}`);
        if (!cancelled) setState({ loading: false, data, err: "" });
      } catch (ex) {
        if (!cancelled) setState({
          loading: false,
          data: null,
          err: formatApiError(ex.response?.data?.detail) || ex.message,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  const s = state.data?.summary || {};
  const events = state.data?.events || [];
  const account = state.data?.account || {};
  const plan = Array.isArray(account.plan) ? account.plan[0] : null;

  // Ordered so the "healthy" metrics come first and the failure modes are
  // clustered together at the right — makes at-a-glance triage faster.
  const cards = [
    { label: "Requests", value: s.requests, tone: "neutral", testId: "email-stat-requests" },
    { label: "Delivered", value: s.delivered, tone: "good", testId: "email-stat-delivered" },
    { label: "Opens (unique)", value: s.uniqueOpens, tone: "good", testId: "email-stat-opens" },
    { label: "Clicks (unique)", value: s.uniqueClicks, tone: "good", testId: "email-stat-clicks" },
    { label: "Hard bounces", value: s.hardBounces, tone: "bad", testId: "email-stat-hardbounces" },
    { label: "Soft bounces", value: s.softBounces, tone: "warn", testId: "email-stat-softbounces" },
    { label: "Blocked", value: s.blocked, tone: "bad", testId: "email-stat-blocked" },
    { label: "Invalid", value: s.invalid, tone: "bad", testId: "email-stat-invalid" },
    { label: "Spam reports", value: s.spamReports, tone: "bad", testId: "email-stat-spam" },
    { label: "Unsubscribed", value: s.unsubscribed, tone: "warn", testId: "email-stat-unsubscribed" },
  ];

  return (
    <div data-testid="email-delivery-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="ccs-label">Range:</span>
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={"text-xs px-2 py-1 border " + (days === d ? "border-amber-400 text-amber-400" : "border-neutral-700 muted-text hover:text-white")}
              data-testid={`email-range-${d}`}
            >
              Last {d} days
            </button>
          ))}
        </div>
        {plan && (
          <div className="text-xs muted-text" data-testid="email-plan">
            Plan: <span className="text-white">{plan.type || "—"}</span>
            {plan.credits != null && (
              <> · Credits: <span className="text-white">{plan.credits}</span></>
            )}
            {account.email && <> · Account: <span className="text-white">{account.email}</span></>}
          </div>
        )}
      </div>

      {state.err && (
        <div className="danger-text text-sm mb-3" data-testid="email-stats-error">
          {state.err}
        </div>
      )}

      {state.loading ? (
        <div className="muted-text text-sm py-8 text-center" data-testid="email-stats-loading">
          Loading email stats from Brevo…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="email-summary-cards">
            {cards.map((c) => (
              <div key={c.label} className="ccs-card">
                <div className="ccs-label mb-1">{c.label}</div>
                <div
                  className={
                    "mono text-2xl font-semibold " +
                    (c.tone === "good"
                      ? "text-emerald-400"
                      : c.tone === "warn"
                      ? "text-amber-400"
                      : c.tone === "bad"
                      ? (Number(c.value || 0) > 0 ? "text-rose-400" : "text-neutral-500")
                      : "text-white")
                  }
                  data-testid={c.testId}
                >
                  {c.value ?? "—"}
                </div>
              </div>
            ))}
          </div>

          <div className="ccs-label mb-2">Recent activity (last 25 events)</div>
          <div className="ccs-table-wrap overflow-auto">
            <table className="ccs-table" data-testid="email-events-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Recipient</th>
                  <th>Event</th>
                  <th>Subject</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev, i) => (
                  <tr key={`${ev.messageId || i}-${ev.event}-${ev.date}`}>
                    <td className="muted-text whitespace-nowrap">{fmtEventDate(ev.date)}</td>
                    <td className="mono text-xs">{ev.email}</td>
                    <td>
                      <EventBadge event={ev.event} />
                    </td>
                    <td className="text-xs muted-text max-w-[280px] truncate">{ev.subject || "—"}</td>
                    <td className="text-xs muted-text max-w-[240px] truncate">{ev.reason || ev.tag || "—"}</td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr>
                    <td colSpan="5" className="muted-text text-center py-8">
                      No email events in this window.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function fmtEventDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}

function EventBadge({ event }) {
  // Colour palette matches the summary cards so a red pill in the log
  // maps 1:1 to a red count above.
  const tone = {
    delivered: "text-emerald-400 border-emerald-800",
    opened: "text-emerald-400 border-emerald-800",
    clicks: "text-emerald-400 border-emerald-800",
    requests: "text-neutral-300 border-neutral-700",
    hardBounces: "text-rose-400 border-rose-800",
    softBounces: "text-amber-400 border-amber-800",
    blocked: "text-rose-400 border-rose-800",
    invalid: "text-rose-400 border-rose-800",
    spam: "text-rose-400 border-rose-800",
    deferred: "text-amber-400 border-amber-800",
    unsubscribed: "text-amber-400 border-amber-800",
    error: "text-rose-400 border-rose-800",
  }[event] || "text-neutral-300 border-neutral-700";
  return (
    <span
      className={"mono text-[10px] uppercase px-2 py-0.5 border " + tone}
      data-testid={`event-badge-${event}`}
    >
      {event}
    </span>
  );
}
